import { Button, type ButtonProps } from 'antd';
import { createStyles } from 'antd-style';
import { forwardRef, type ComponentRef, type HTMLAttributes } from 'react';
import { CloseOutlined } from './icon/tabler';

const ITEM_SIZE = 28;
const PADDING_X = 7;
const PADDING_Y = 4;

// The labeled canvas Dock and media previews share these controls and tokens.
const useStyles = createStyles(({ token, css }) => ({
  bar: css`
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 2px;
    width: max-content;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    &[data-wrap="true"] { flex-wrap: wrap; justify-content: center; max-height: 40dvh; overflow-y: auto; }
    padding: ${PADDING_Y}px ${PADDING_X}px;
    border-radius: 12px;
    background: ${token.colorBgElevated};
    border: 1px solid ${token.colorBorderOverlay ?? token.colorBorder};
    box-shadow: ${token.boxShadowPanel ?? token.boxShadowSecondary};
  `,
  button: css`
    height: ${ITEM_SIZE}px !important;
    min-width: ${ITEM_SIZE}px;
    padding: 0 9px !important;
    border-radius: 8px;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: ${token.colorTextSecondary};
    background: transparent !important;
    box-shadow: none !important;
    transition:
      color 120ms ease-out,
      background-color 120ms ease-out;
    .anticon {
      font-size: 16px;
      line-height: 1;
    }
    &:not(:disabled):hover {
      color: ${token.colorText};
      background: ${token.colorFillTertiary} !important;
    }
    &[data-danger="true"] { color: ${token.colorError}; }
    &:disabled {
      color: ${token.colorTextDisabled};
      background: transparent !important;
    }
  `,
  iconOnly: css`
    width: ${ITEM_SIZE}px;
    min-width: ${ITEM_SIZE}px;
    padding: 0 !important;
    gap: 0;
  `,
  text: css`
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
    white-space: nowrap;
  `,
  divider: css`
    width: 1px;
    align-self: center;
    height: 18px;
    margin: 0 2px;
    background: ${token.colorBorder};
    border-radius: 1px;
  `,
}));

export const ActionDockButton = forwardRef<
  ComponentRef<typeof Button>,
  ButtonProps & { iconOnly?: boolean }
>(function ActionDockButton(
  { children, iconOnly, className, danger, ...props },
  ref,
) {
  const { styles, cx } = useStyles();
  return (
    <Button
      {...props}
      ref={ref}
      type="text"
      size="small"
      danger={danger}
      data-danger={danger}
      className={cx(styles.button, iconOnly && styles.iconOnly, className)}
    >
      {!iconOnly && children ? (
        <span className={styles.text}>{children}</span>
      ) : null}
    </Button>
  );
});

export function ActionDockDivider() {
  const { styles } = useStyles();
  return <div className={styles.divider} aria-hidden />;
}

export function ActionDock({
  children,
  className,
  wrap = false,
  onClose,
  closeLabel = 'Close',
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  wrap?: boolean;
  onClose?: () => void;
  closeLabel?: string;
}) {
  const { styles, cx } = useStyles();
  return (
    <div
      {...props}
      role="toolbar"
      data-wrap={wrap}
      className={cx(styles.bar, className)}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {children}
      {onClose && (
        <ActionDockButton
          icon={<CloseOutlined />}
          iconOnly
          aria-label={closeLabel}
          title={closeLabel}
          onClick={onClose}
        />
      )}
    </div>
  );
}
